/**
 * MethodStep — step 1 of the Add Activities wizard.
 *
 * Renders the 6 intent cards split into Scope 1+2 / Scope 3 rows. Each
 * card carries a few example-category pills so users can sense what fits
 * inside. Click on any intent auto-advances to step 2 — there is no
 * "Choose your method" sub-row anymore (the method buttons live in the
 * step-2 toolbar) and there is no inline provider section (those moved
 * into the `ConnectProviderModal` triggered from step 2).
 */

import { type ElementType } from 'react';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { Card, CardContent } from '@g17eco/ui-modern/components/ui/card';
import { cn } from '@g17eco/ui-modern/lib/utils';
import { INTENT_CARDS, type DataIntent } from './types';

interface MethodStepProps {
  /** Called when the user picks an intent. The parent owns step-2 routing. */
  onPickIntent: (intent: DataIntent) => void;
}

const ScopeRowHeader = ({ label, tone }: { label: string; tone: 'scope1-2' | 'scope3' }) => (
  <div
    className={cn(
      'tw:rounded-t-md tw:px-3 tw:py-1.5 tw:flex tw:items-center tw:gap-2',
      tone === 'scope1-2' ? 'tw:bg-emerald-50/40' : 'tw:bg-slate-50',
    )}
  >
    <span
      className={cn(
        'tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-wider',
        tone === 'scope1-2' ? 'tw:text-emerald-800' : 'tw:text-slate-700',
      )}
    >
      {label}
    </span>
    <span
      className={cn(
        'tw:h-[2px] tw:flex-1 tw:rounded-full',
        tone === 'scope1-2' ? 'tw:bg-emerald-200' : 'tw:bg-slate-300',
      )}
    />
  </div>
);

const IntentTile = ({
  id,
  title,
  caption,
  Icon,
  iconBg,
  iconColor,
  exampleCategories,
  onClick,
}: {
  id: DataIntent;
  title: string;
  caption: string;
  Icon: ElementType;
  iconBg: string;
  iconColor: string;
  exampleCategories: string[];
  onClick: () => void;
}) => (
  <Card
    onClick={onClick}
    data-intent={id}
    className={cn('tw:flex tw:flex-row tw:items-center tw:gap-0 tw:text-left tw:cursor-pointer tw:hover:-translate-y-[1px]', iconBg)}
    style={{ padding: 0, minHeight: 150 }}
  >
    <div
      className={cn('tw:inline-flex tw:items-center tw:justify-center tw:rounded-full tw:text-center', iconBg)}
      style={{ width: 66, height: 66, minWidth: 66, minHeight: 66 }}
    >
      <Icon className={cn('tw:size-8', iconColor)} />
    </div>
    <div className='tw:p-3 tw:flex tw:flex-col tw:justify-center tw:h-full tw:w-full tw:bg-white tw:rounded-xl'>
      <div className='tw:font-bold tw:text-[#1a1d1f] tw:leading-snug tw:text-lg'>{title}</div>
      <div className='tw:text-muted-foreground tw:text-xs'>{caption}</div>
      {exampleCategories.length > 0 && (
        <div className='tw:flex tw:flex-wrap tw:gap-1 tw:mt-2'>
          {exampleCategories.map((label) => (
            <span
              key={label}
              className={cn('tw:inline-flex tw:items-center tw:rounded-full tw:px-2 tw:py-0.5 tw:text-[10.5px] tw:text-muted-foreground', iconBg)}
            >
              {label}
            </span>
          ))}
        </div>
      )}
      </div>
    </Card>
);

export const MethodStep = ({ onPickIntent }: MethodStepProps) => {
  const scope12Cards = INTENT_CARDS.filter((c) => c.scopeGroup === 'scope1-2');
  const scope3Cards = INTENT_CARDS.filter((c) => c.scopeGroup === 'scope3');

  return (
    <Card className='tw:mb-6 tw:shadow-xs'>
      <CardContent className='tw:p-6'>
        <h5 className='tw:font-semibold tw:mb-1'>How would you like to add?</h5>
        <p className='tw:text-sm tw:text-muted-foreground tw:mb-6'>
          Pick a category — we'll guide you the process.
        </p>

        <div className='tw:flex tw:flex-col tw:gap-5'>
          <div>
            <ScopeRowHeader label='Scope 1 + 2' tone='scope1-2' />
            <div className='tw:grid tw:grid-cols-1 tw:sm:grid-cols-2 tw:gap-6 tw:mt-2'>
              {scope12Cards.map((card) => (
                <IntentTile
                  key={card.id}
                  id={card.id}
                  title={card.title}
                  caption={card.caption}
                  Icon={card.Icon}
                  iconBg={card.iconBg}
                  iconColor={card.iconColor}
                  exampleCategories={card.exampleCategories}
                  onClick={() => onPickIntent(card.id)}
                />
              ))}
            </div>
          </div>

          <div className='tw:mt-4'>
            <ScopeRowHeader label='Scope 3' tone='scope3' />
            <div className='tw:grid tw:grid-cols-1 tw:sm:grid-cols-2 tw:md:grid-cols-2 tw:gap-6 tw:mt-2'>
              {scope3Cards.map((card) => (
                <IntentTile
                  key={card.id}
                  id={card.id}
                  title={card.title}
                  caption={card.caption}
                  Icon={card.Icon}
                  iconBg={card.iconBg}
                  iconColor={card.iconColor}
                  exampleCategories={card.exampleCategories}
                  onClick={() => onPickIntent(card.id)}
                />
              ))}
            </div>
          </div>
        </div>

        <div className='tw:mt-6 tw:flex tw:justify-end'>
          <Button variant='ghost' size='sm' disabled>
            Need help choosing?
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
